export type UserRole =
  | 'consumer'
  | 'professional_owner'
  | 'professional_employee'
  | 'customer_success'
  | 'admin'
  | 'system';

export type UserStatus =
  | 'pending_email_verification'
  | 'active'
  | 'suspended'
  | 'deleted_pending'
  | 'deleted';

export type ParsedAuthClaims = {
  role: UserRole | null;
  status: UserStatus | null;
  businessId: string | null;
  isInteractive: boolean;
};

const roles: readonly string[] = [
  'consumer', 'professional_owner', 'professional_employee',
  'customer_success', 'admin', 'system',
];
const statuses: readonly string[] = [
  'pending_email_verification', 'active', 'suspended', 'deleted_pending', 'deleted',
];
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const neutral = (): ParsedAuthClaims => ({
  role: null,
  status: null,
  businessId: null,
  isInteractive: false,
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && roles.includes(value);
}

function isUserStatus(value: unknown): value is UserStatus {
  return typeof value === 'string' && statuses.includes(value);
}

/** Lê claims já decodificados para a UI; a autorização de dados é decidida pela RLS. */
export function parseAuthClaims(payload: unknown): ParsedAuthClaims {
  if (!isRecord(payload) || !isRecord(payload.app_metadata)) return neutral();

  const { user_role: role, user_status: status, business_id: businessId } = payload;
  if (
    !isUserRole(role) ||
    !isUserStatus(status) ||
    payload.app_metadata.role !== role ||
    (businessId !== null && (typeof businessId !== 'string' || !uuidPattern.test(businessId)))
  ) return neutral();

  if (status !== 'active') return neutral();

  return {
    role,
    status,
    businessId,
    isInteractive: role !== 'system',
  };
}

/**
 * Decodifica o payload do access token para a UI decidir **navegação**.
 *
 * 🔴 Os claims do Custom Access Token Hook existem só no JWT: o GoTrue não os copia para
 * `session.user` (verificado no stack local na T1.17). Isto não valida o token — a assinatura é
 * verificada pelo servidor a cada pedido, e a autorização de dados é da RLS.
 */
export function decodeAccessTokenPayload(token: unknown): unknown {
  if (typeof token !== 'string') return null;
  const segment = token.split('.')[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  } catch {
    return null;
  }
}

type SessionLike = { access_token?: unknown } | null | undefined;

/** Claims de navegação da sessão, lidos do access token. */
export function claimsFromSession(session: SessionLike): ParsedAuthClaims {
  return parseAuthClaims(decodeAccessTokenPayload(session?.access_token));
}

/**
 * Status **bruto** do access token, sem a neutralização de `parseAuthClaims`. Existe para o login
 * reconhecer uma conta em carência (`deleted_pending`) e reativá-la antes de liberar a sessão.
 */
export function accessTokenStatus(session: SessionLike): UserStatus | null {
  const payload = decodeAccessTokenPayload(session?.access_token);
  if (!isRecord(payload)) return null;
  return isUserStatus(payload.user_status) ? payload.user_status : null;
}
